import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import Sidebar from "../components/SideBar/SiderBar";
import HeaderBar from "../components/HeadBar/HeadBar";
import QuickSettings from "../components/QuickSettings";
import HelpCenterDrawer from "../components/HelpCenter/HelpCenterDrawer";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";

export default function AdminLayout() {
  const navigate = useNavigate();
  const [sidebarVisible, setSidebarVisible] = useState(window.innerWidth >= 1024);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [helpVisible, setHelpVisible] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true";
  });
  const [showUpdates, setShowUpdates] = useState(false);
  const [helpTab, setHelpTab] = useState("faq");

  useEffect(() => {
    const handleOpenHelp = (e) => {
      if (e?.detail?.tab) {
        setHelpTab(e.detail.tab);
      }
      setHelpVisible(true);
    };
    window.addEventListener("open-help-center", handleOpenHelp);
    return () => window.removeEventListener("open-help-center", handleOpenHelp);
  }, []);

  useEffect(() => {
    const hasSeen = localStorage.getItem("hasSeenUpdatePopup_2026_06");
    if (!hasSeen) {
      setShowUpdates(true);
    }
  }, []);

  const handleCloseUpdates = () => {
    localStorage.setItem("hasSeenUpdatePopup_2026_06", "true");
    setShowUpdates(false);
  };

  // Pencere boyutu değiştiğinde sidebar durumunu güncelle (isteğe bağlı ama iyi olur)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarVisible(true);
      } else {
        setSidebarVisible(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Global Keyboard Shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // F1: Ana Sayfa
      if (e.key === "F1") {
        e.preventDefault();
        navigate("/");
      }
      // F2: Satış Yap
      if (e.key === "F2") {
        // If we are on pages where F2 is used for Saving, don't navigate
        const f2SavePages = ["/sales/productsale", "/suppliers/purchase", "/products/add"];
        if (f2SavePages.includes(window.location.pathname)) return;

        e.preventDefault();
        navigate("/sales/productsale");
      }
      // F3: Müşteri Listesi
      if (e.key === "F3") {
        e.preventDefault();
        navigate("/customers");
      }
      // F4: Tedarikçi Listesi
      if (e.key === "F4") {
        e.preventDefault();
        navigate("/suppliers");
      }
      // ESC: Bir önceki sayfaya git
      if (e.key === "Escape") {
        // Eğer ana sayfadaysak bir şey yapma
        if (window.location.pathname === "/") return;

        // Eğer açık bir modal, dialog, takvim veya sidebar varsa navigasyon yapma (ESC önce onları kapatsın)
        const hasOverlay = document.querySelector(
          ".p-component-overlay, .p-dialog-mask, .p-sidebar-mask, .p-datepicker, .p-overlaypanel"
        );
        if (hasOverlay) return;

        e.preventDefault();
        navigate(-1);
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown, true);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown, true);
  }, [navigate]);

  const handleSidebarToggle = () => {
    if (window.innerWidth >= 1024) {
      const newState = !isCollapsed;
      setIsCollapsed(newState);
      localStorage.setItem("sidebarCollapsed", newState);
    } else {
      setSidebarVisible(!sidebarVisible);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] dark:bg-slate-900 flex overflow-x-clip transition-colors duration-300">
      <QuickSettings visible={settingsVisible} onHide={() => setSettingsVisible(false)} />
      <HelpCenterDrawer visible={helpVisible} initialTab={helpTab} onHide={() => setHelpVisible(false)} />

      {/* RELEASE UPDATES DIALOG */}
      <Dialog
        visible={showUpdates}
        onHide={handleCloseUpdates}
        showHeader={false}
        modal
        style={{ width: '90%', maxWidth: '750px' }}
        className="rounded-3xl overflow-hidden border-0 shadow-2xl"
        contentClassName="p-0 bg-slate-50 dark:bg-slate-900 border-0 rounded-3xl"
        appendTo="self"
      >
        <div className="flex flex-col h-[85vh] max-h-[750px] overflow-hidden rounded-3xl bg-slate-50 dark:bg-slate-950">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-700 via-indigo-600 to-violet-600 dark:from-indigo-950 dark:to-indigo-900 text-white p-6 relative shrink-0">
            <div className="absolute top-4 right-4 opacity-10">
              <i className="pi pi-rocket text-8xl"></i>
            </div>
            <span className="bg-white/20 text-white text-[12px] font-black uppercase tracking-widest px-3 py-1 rounded-full border border-white/20">
              Yeni Sürüm Yayında
            </span>
            <h2 className="text-xl lg:text-2xl font-black mt-3 mb-1 tracking-tight text-white">
              KasamBenim’de Yeni Güncellemeler Yayında 🚀
            </h2>
            <p className="text-white/80 text-s font-semibold leading-relaxed max-w-xl">
              KasamBenim'i sizler için daha kullanışlı, hızlı ve anlaşılır hale getirdik. İşte bu sürümde yer alan yenilikler:
            </p>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-3 space-y-4 custom-scrollbar bg-slate-50/50 dark:bg-slate-900/40">
            {/* Intro */}
            <div className="p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
              <p className="text-s text-slate-600 dark:text-slate-400 font-medium leading-relaxed m-0">
                Merhaba, KasamBenim’i daha kullanışlı, daha hızlı ve daha anlaşılır hale getirmek için geliştirmelerimize devam ediyoruz. Bu güncellemede özellikle ekran tasarımları, müşteri detay görünümü, finansal hesap işlemleri, şube raporları, stok yönetimi ve listeleme deneyimi üzerinde önemli iyileştirmeler yaptık.
              </p>
            </div>

            {/* Updates list */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Item 1 */}
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="!h-8 !w-8 !min-w-[32px] shrink-0 aspect-square rounded-xl bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                      <i className="pi pi-desktop text-md"></i>
                    </div>
                    <h4 className="text-s font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                      Daha Modern Ekranlar
                    </h4>
                  </div>
                  <p className="text-[14px] text-slate-600 dark:text-slate-400 leading-relaxed">
                    Stok hareketleri, stok yönetimi, kritik stoklar, depo listesi, teklifler ve yeni teklif ekranlarında başlık alanları yenilendi. Sayfalar artık daha düzenli, daha modern ve karanlık modla daha uyumlu bir görünüme sahip.
                  </p>
                  <p className="text-[14px] text-slate-600 dark:text-slate-400 leading-relaxed mt-1.5">
                    Açık/koyu tema kontrast uyumu; tablo yazıları, filtre alanları ve formlarda iyileştirilerek okunabilirlik artırıldı.
                  </p>
                </div>
              </div>

              {/* Item 2 */}
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="!h-8 !w-8 !min-w-[32px] shrink-0 aspect-square rounded-xl bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                      <i className="pi pi-user text-md"></i>
                    </div>
                    <h4 className="text-s font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                      Müşteri Görünüm Tercihi
                    </h4>
                  </div>
                  <p className="text-[14px] text-slate-600 dark:text-slate-400 leading-relaxed">
                    Yeni eklenen <strong>Görünüm Ayarları</strong> sayfasıyla müşteri detay ekranını kendi tercihinize göre <strong>Fatura odaklı</strong> veya <strong>Alınan Ürünler odaklı</strong> olarak açılacak şekilde özelleştirebilirsiniz.
                  </p>
                  <p className="text-[14px] text-slate-600 dark:text-slate-400 leading-relaxed mt-1.5">
                    Açık/koyu tema uyumu iyileştirilirken faturalar alanına fatura no/tarih bazlı hızlı arama ve <strong>"Açık Faturaları Göster"</strong> filtresi eklendi.
                  </p>
                </div>
              </div>

              {/* Item 3 */}
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="!h-8 !w-8 !min-w-[32px] shrink-0 aspect-square rounded-xl bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                      <i className="pi pi-wallet text-md"></i>
                    </div>
                    <h4 className="text-s font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                      Manuel Para Giriş/Çıkışı
                    </h4>
                  </div>
                  <p className="text-[14px] text-slate-600 dark:text-slate-400 leading-relaxed">
                    Kasa ve banka hesaplarınız üzerinde manuel para giriş veya çıkışı yapabilmeniz için yeni işlem ekranları eklendi.
                  </p>
                  <p className="text-[14px] text-slate-600 dark:text-slate-400 leading-relaxed mt-1.5">
                    Hesap detay sayfasından tarih, tutar ve açıklama bilgisi girerek esnek hareketler oluşturabilirsiniz.
                  </p>
                </div>
              </div>

              {/* Item 4 */}
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="!h-8 !w-8 !min-w-[32px] shrink-0 aspect-square rounded-xl bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                      <i className="pi pi-sync text-md"></i>
                    </div>
                    <h4 className="text-s font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                      Gelişmiş Bakiye Transferi
                    </h4>
                  </div>
                  <p className="text-[14px] text-slate-600 dark:text-slate-400 leading-relaxed">
                    Hesaplar arası bakiye transferi ekranı yenilendi. Seçim listelerinde hesapların türü, adı ve anlık bakiye bilgisi artık daha düzenli ve kolay seçilebilir şekilde sunuluyor.
                  </p>
                </div>
              </div>

              {/* Item 5 */}
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="!h-8 !w-8 !min-w-[32px] shrink-0 aspect-square rounded-xl bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                      <i className="pi pi-chart-bar text-md"></i>
                    </div>
                    <h4 className="text-s font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                      Şube Bazlı Raporlama
                    </h4>
                  </div>
                  <p className="text-[14px] text-slate-600 dark:text-slate-400 leading-relaxed">
                    Yeni <strong>Şube Raporu</strong> sayfası ile şubelerinizin performansını karşılaştırmalı takip edebilirsiniz. Alışlar, satışlar, tahsilatlar, giderler, net akış ve bakiyeler özet kartlar ve grafiklerle sunuluyor.
                  </p>
                </div>
              </div>

              {/* Item 6 */}
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="!h-8 !w-8 !min-w-[32px] shrink-0 aspect-square rounded-xl bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                      <i className="pi pi-truck text-md"></i>
                    </div>
                    <h4 className="text-s font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                      Tedarikçi Listesi & Vade
                    </h4>
                  </div>
                  <p className="text-[14px] text-slate-600 dark:text-slate-400 leading-relaxed">
                    Tedarikçi listesi sadeleştirilerek kompakt hale getirildi. En yakın ödenmemiş vadeli faturanın tarihi eklenerek günü geçmiş vadeler belirgin renklerle gösterilmeye başlandı. Telefon ve e-posta kolonları kaldırıldı.
                  </p>
                </div>
              </div>

              {/* Item 7 */}
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="!h-8 !w-8 !min-w-[32px] shrink-0 aspect-square rounded-xl bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                      <i className="pi pi-tags text-md"></i>
                    </div>
                    <h4 className="text-s font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                      Ürün Listesi Düzenlemesi
                    </h4>
                  </div>
                  <p className="text-[14px] text-slate-600 dark:text-slate-400 leading-relaxed">
                    İşlem butonları daha kompakt bir yapıya alındı. Ürünlerin durum bilgileri yeni tasarımıyla daha net şekilde okunabilir duruma getirildi.
                  </p>
                </div>
              </div>

              {/* Item 8 */}
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="!h-8 !w-8 !min-w-[32px] shrink-0 aspect-square rounded-xl bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                      <i className="pi pi-search text-md"></i>
                    </div>
                    <h4 className="text-s font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                      Arama Geçmişi Belleği
                    </h4>
                  </div>
                  <p className="text-[14px] text-slate-600 dark:text-slate-400 leading-relaxed">
                    Müşteri, tedarikçi ve ürün listelerindeki aramalarınız artık tarayıcıda hatırlanıyor. Sayfa değiştirip geri döndüğünüzde kaldığınız yerden hızlıca devam edebilirsiniz. Müşteri listesine <strong>"Temizle"</strong> butonu da eklendi.
                  </p>
                </div>
              </div>

              {/* Item 9 */}
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm flex flex-col justify-between md:col-span-2">
                <div>
                  <div className="flex items-center   mb-2">
                    <div className="!h-8 !w-8 !min-w-[32px] shrink-0 aspect-square rounded-xl bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                      <i className="pi pi-box text-md"></i>
                    </div>
                    <h4 className="text-s font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                      Stok Yönetimi & Genel İyileştirmeler
                    </h4>
                  </div>
                  <p className="text-[14px] text-slate-600 dark:text-slate-400 leading-relaxed">
                    Stok yönetimi ve manuel düzeltme ekranlarındaki form alanları ve işlem türü seçim kartları iyileştirildi. Karanlık moddaki kontrast sorunları ve okunabilirliği zor alanlar düzenlendi. Ayrıca müşteri şube ve fiyat seviyesi kayıt sorunu düzeltilerek sayfa geçişleri daha akıcı hale getirildi.
                  </p>
                </div>
              </div>

            </div>

            {/* Closing note */}
            <div className="p-4 bg-slate-100 dark:bg-slate-900 border border-slate-200/50 dark:border-slate-800 text-center rounded-2xl">
              <p className="text-s text-slate-600 dark:text-slate-400 font-bold m-0 leading-relaxed">
                KasamBenim’i gerçek kullanıcı ihtiyaçlarına göre geliştirmeye devam ediyoruz. Görüşleriniz, önerileriniz ve ihtiyaçlarınız bizim için çok değerli.
              </p>
              <p className="text-[12px] text-slate-500 dark:text-slate-500 mt-2 m-0">
                Herhangi bir konuda bize iletisim@kasambenim.com üzerinden ulaşabilirsiniz.
              </p>
              <p className="text-s font-black text-indigo-600 dark:text-indigo-400 mt-3 m-0">
                İyi çalışmalar dileriz,<br /><br />KasamBenim Ekibi
              </p>
            </div>
          </div>

          {/* Footer Button */}
          <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 shrink-0 rounded-b-3xl flex justify-center">
            <Button
              label="Harika, Keşfetmeye Başla!"
              icon="pi pi-check"
              onClick={handleCloseUpdates}
              className="w-full py-3 rounded-2xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white border-none shadow-md hover:shadow-lg transition-all animate-pulse"
            />
          </div>
        </div>
      </Dialog>

      {/* SIDEBAR */}
      <div
        className={`fixed inset-y-0 left-0 z-[2000] no-print
          ${sidebarVisible ? "translate-x-0 shadow-2xl" : "-translate-x-full shadow-none"}
          lg:relative lg:translate-x-0 lg:shadow-none lg:opacity-100
          ${isCollapsed ? "w-[80px] !overflow-visible !transform-none" : "w-[260px] overflow-hidden transition-all duration-300 transform"}
        `}
      >
        <Sidebar visible={true} isCollapsed={isCollapsed} onClose={() => setSidebarVisible(false)} onOpenHelp={() => setHelpVisible(true)} />
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        <div className="no-print">
          <HeaderBar
            onSidebarToggle={handleSidebarToggle}
            onSettingsToggle={() => setSettingsVisible(true)}
          />
        </div>

        <main className="flex-1 overflow-y-auto custom-scrollbar bg-[#f8fafc] dark:bg-slate-900 p-3 lg:p-4 transition-colors duration-300">
          {/* Mobil Overlay */}
          {sidebarVisible && (
            <div
              onClick={() => setSidebarVisible(false)}
              className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[1050] lg:hidden transition-opacity duration-300"
            />
          )}

          <div className="max-w-[1600px] mx-auto animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
